import { z } from 'zod';
import { GeoPoint } from './capture';
import { EntityId } from './ids';

/**
 * Includes `in_review` and `rejected` because the server sends them.
 *
 * They were missing, and `ServerSessionRow.status` is an unconstrained
 * string, so a reviewed session landed in the phone's `sessions` table and
 * was then dropped on the way out: `SessionSummary.safeParse` failed on the
 * status and the row was skipped. The visible effect was that a session
 * disappeared from the phone the moment a reviewer opened or rejected it —
 * the inspector's own work, gone, with no way to tell whether it had been
 * approved or thrown out.
 */
export const SessionStatus = z.enum([
  'draft',
  'active',
  'submitted',
  'in_review',
  'approved',
  'rejected',
  // Sent back for a fix rather than thrown out, and the only status a session
  // can leave by being submitted a second time. `rejected` is terminal; this
  // deliberately is not.
  'changes_requested',
  'archived',
]);
export type SessionStatus = z.infer<typeof SessionStatus>;

export const Session = z.object({
  id: EntityId,
  title: z.string().min(1).max(200),
  /** Which template drove this session (nullable for free-form). */
  templateId: z.string().nullable(),
  /**
   * Which project this session is filed under.
   *
   * Nullable and defaulted, for the same reason the server column is: capture
   * has never required a project and still does not. A session created with
   * no active project set is filed by a manager later, or not at all.
   */
  projectId: z.string().nullable().default(null),
  status: SessionStatus,
  startLocation: GeoPoint.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  submittedAt: z.string().datetime().nullable().default(null),
  approvedAt: z.string().datetime().nullable().default(null),
  /** User id who owns this session. */
  ownerId: z.string(),
  /** Optional reviewer assignment. */
  reviewerId: z.string().nullable().default(null),
  /** Free-form metadata (asset ids, project codes, etc). */
  meta: z.record(z.string()).default({}),
  /**
   * Why a reviewer sent this back, or turned it down.
   *
   * Cleared by the server on resubmission — it described the submission that
   * came back, so keeping it would show the inspector a complaint about work
   * they have just redone.
   */
  reviewNote: z.string().nullable().default(null),
  /**
   * Template field ids the note is about.
   *
   * The machine-readable half beside the prose, never instead of it: the form
   * can mark these fields and offer to open them, while the note says what is
   * actually wrong. Empty is normal — plenty of send-backs are about a
   * photograph, an access problem, or the whole submission.
   */
  flaggedFields: z.array(z.string()).default([]),
});
export type Session = z.infer<typeof Session>;

export const SessionSummary = Session.pick({
  id: true,
  title: true,
  status: true,
  // Picked so the hub can show "in this project" without a second read per
  // row — the list it filters is already in hand.
  projectId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  captureCount: z.number().int().nonnegative(),
  pendingSyncCount: z.number().int().nonnegative(),
});
export type SessionSummary = z.infer<typeof SessionSummary>;
